"use client";
import React, { useRef, useState } from "react";
import { HoveredLink, Menu, MenuItem, ProductItem } from "./NavbarMenu";
// import { cn } from "@/utils/cn";
import { cn } from "./utils/cn";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  ProfileAvatarParent,
  SignUpBtnParent,
} from "../../components/allComponents/AllComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentUser,
  // selectCurrentUser,
  selectIsAuthenticated,
  // selectUserFirstName,
} from "../../redux/selectors/userSelector";
import { AppDispatch } from "../../redux/store/store";
import { motion, AnimatePresence } from "framer-motion";
import { setLanguage } from "../../redux/slices/languageSlice";
import { selectCurrentLanguage } from "../../redux/selectors/languageSelector";
import i18n from "../../config/i18n";
import { logOut } from "../../redux/actions/userActions";

const LANGUAGES = [
  { code: "en" as const, label: "English", flag: "🇺🇸", short: "EN" },
  { code: "es" as const, label: "Español", flag: "🇪🇸", short: "ES" },
  { code: "fr" as const, label: "Français", flag: "🇫🇷", short: "FR" },
  { code: "mx" as const, label: "Español (MX)", flag: "🇲🇽", short: "MX" },
];

function LanguageSwitcher() {
  const dispatch = useDispatch<AppDispatch>();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const current = LANGUAGES.find((l) => l.code === currentLanguage) ?? LANGUAGES[0];

  const handleSelect = (code: "en" | "es" | "fr" | "mx") => {
    dispatch(setLanguage(code));
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative flex items-center"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') handleMouseEnter(); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') handleMouseLeave(); }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-all duration-200 cursor-pointer select-none"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="tracking-wide">{current.short}</span>
        <svg
          className={`w-3 h-3 opacity-70 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 8 }}
            transition={{ type: "spring", mass: 0.5, damping: 11.5, stiffness: 100, restDelta: 0.001 }}
            className="absolute top-[calc(100%_+_0.75rem)] right-0 z-50 min-w-[148px] rounded-2xl border border-white/20 bg-black shadow-2xl overflow-hidden"
            role="listbox"
          >
            {LANGUAGES.map((lang) => {
              const isActive = lang.code === currentLanguage;
              return (
                <button
                  key={lang.code}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors duration-150 cursor-pointer ${isActive
                    ? "bg-white/15 text-white font-semibold"
                    : "text-white/80 hover:bg-white/10 hover:text-white"
                    }`}
                >
                  <span className="text-base leading-none">{lang.flag}</span>
                  <span className="flex-1">{lang.label}</span>
                  {isActive && (
                    <svg className="w-3.5 h-3.5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function NavBarContainer() {
  return (
    <div className="relative w-full flex items-center justify-center">
      <Navbar className="top-2" />
    </div>
  );
}

export function Navbar({ className }: { className?: string }) {
  const userIsAuthenticated = useSelector(selectIsAuthenticated);
  const userData = useSelector(selectCurrentUser);
  const [active, setActive] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const navRef = useRef<HTMLDivElement>(null);

  // Close any open dropdown when tapping outside on mobile
  React.useEffect(() => {
    if (!active) return;
    const handleOutside = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActive(null);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [active]);

  const handleLogOut = async () => {
    try {
      await dispatch(logOut()).unwrap(); // Ensure logout completes

      localStorage.setItem("recentlyLoggedOut", "true"); // Set flag

      localStorage.clear();

      sessionStorage.clear();

      navigate("/circle/sign-in"); // Navigate after logout
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <div ref={navRef} className={cn("top-10 inset-x-0 mx-auto z-50", className)}>
      <Menu setActive={setActive}>
        {/* <MenuItem setActive={setActive} active={active} item="Services">
          <div className="flex flex-col space-y-1 md:space-y-4 text-sm">
            <HoveredLink href="/web-dev">Web Development</HoveredLink>
            <HoveredLink href="/interface-design">Interface Design</HoveredLink>
            <HoveredLink href="/seo">Search Engine Optimization</HoveredLink>
            <HoveredLink href="/branding">Branding</HoveredLink>
          </div>
        </MenuItem> */}
        {/* <MenuItem setActive={setActive} active={active} item="Products">
          <div className="text-white  text-sm grid grid-cols-2 gap-10 p-4">
            <ProductItem
              title="Algochurn"
              href="https://algochurn.com"
              src="https://assets.aceternity.com/demos/algochurn.webp"
              description="Prepare for tech interviews like never before."
            />
            <ProductItem
              title="Tailwind Master Kit"
              href="https://tailwindmasterkit.com"
              src="https://assets.aceternity.com/demos/tailwindmasterkit.webp"
              description="Production ready Tailwind css components for your next project"
            />
            <ProductItem
              title="Moonbeam"
              href="https://gomoonbeam.com"
              src="https://assets.aceternity.com/demos/Screenshot+2024-02-21+at+11.51.31%E2%80%AFPM.png"
              description="Never write from scratch again. Go from idea to blog in minutes."
            />
            <ProductItem
              title="Rogue"
              href="https://userogue.com"
              src="https://assets.aceternity.com/demos/Screenshot+2024-02-21+at+11.47.07%E2%80%AFPM.png"
              description="Respond to government RFPs, RFIs and RFQs 10x faster using AI"
            />
          </div>
        </MenuItem> */}
        {/* Products dropdown — visible to all users */}
        <MenuItem setActive={setActive} active={active} item="Products">
          <div className="flex flex-col p-1 w-[min(288px,calc(100vw-3rem))] md:min-w-[288px]">
            {/* Savings Circles */}
            <Link
              to="/circle"
              onClick={() => setActive(null)}
              className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.06] transition-colors duration-150"
            >
              <div className="w-9 h-9 rounded-lg bg-[#1ec195]/[0.15] border border-[#1ec195]/[0.25] flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1ec195" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                  <path d="M7 15h2" />
                  <path d="M11 15h4" />
                </svg>
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">Savings Circles</p>
                <p className="text-white/50 text-xs mt-0.5 leading-snug">Save together, rotate payouts, build community wealth</p>
              </div>
            </Link>

            <div className="border-t border-white/[0.08] my-1" />

            {/* FundsyFi Business */}
            <Link
              to="/business"
              onClick={() => setActive(null)}
              className="flex items-start gap-3 p-3 rounded-xl hover:bg-[#ffcc00]/[0.05] transition-colors duration-150"
            >
              <div className="w-9 h-9 rounded-lg bg-[#ffcc00]/[0.12] border border-[#ffcc00]/[0.20] flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b8860b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-bold text-sm leading-tight">FundsyFi Business</p>
                </div>
                <p className="text-white/50 text-xs mt-0.5 leading-snug">Recover overdue invoices &amp; get paid faster</p>
              </div>
            </Link>
          </div>
        </MenuItem>

        {userIsAuthenticated && (
          <MenuItem setActive={setActive} active={active} item="Actions">
            <div className="flex flex-col space-y-1 md:space-y-4 text-sm">
              {/* <HoveredLink href="/">Home</HoveredLink> */}
              <HoveredLink href={`/user/${userData?._id}`}>
                My Account
              </HoveredLink>
              <HoveredLink href="/circles">Circles</HoveredLink>
            </div>
          </MenuItem>
        )}

        {/* {!userIsAuthenticated && (
          <MenuItem setActive={setActive} active={active} item="Pricing">
            <div className="flex flex-col space-y-1 md:space-y-4 text-sm">
              <HoveredLink href="/hobby">Hobby</HoveredLink>
              <HoveredLink href="/individual">Individual</HoveredLink>
              <HoveredLink href="/team">Team</HoveredLink>
              <HoveredLink href="/enterprise">Enterprise</HoveredLink>
            </div>
          </MenuItem>
        )} */}

        {!userIsAuthenticated && <SignUpBtnParent />}

        {userIsAuthenticated && <ProfileAvatarParent />}

        {userIsAuthenticated && (
          <Button label={"Sign Out"} showDot={false} onClick={handleLogOut} />
        )}

        <LanguageSwitcher />

        {/*  */}
      </Menu>
    </div>
  );
}
